import React, { useEffect, useState } from "react";

import { useMenuCategories } from "shared/hooks/useMenuCategories";
import { useMenuOptions } from "shared/hooks/useMenuOptions";
import { localizedText } from "shared/lib/localizedMap";
import { Button, LabeledField, Modal, Select, Stack, TextInput } from "shared/ui";

import { useMenuEditorContext } from "features/MenuEditor/context";

/*
* Копия позиции в это же или в другое меню. Монтируется только когда диалог открыт
* (см. корень фичи): иначе список категорий выбранного меню тянулся бы на каждом рендере
* экрана, а он нужен ровно здесь.
* */
export const CopyItemDialog = () => {
    const state = useMenuEditorContext();
    const item = state.dialog.kind === "copyItem" ? state.dialog.item : null;

    const { menuOptions } = useMenuOptions();
    const [targetMenuId, setTargetMenuId] = useState<number>(state.menuId);
    const [targetCategoryId, setTargetCategoryId] = useState<number | null>(null);
    const [name, setName] = useState<string>("");

    const { categories: targetCategories, loading: categoriesLoading } =
        useMenuCategories(targetMenuId);

    useEffect(() => {
        if (!item) return;

        setTargetMenuId(state.menuId);
        // Копия в ту же категорию получает суффикс — иначе в списке два одинаковых блюда.
        setName(`${localizedText(item.name, state.locale)} (1)`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item?.id]);

    // Категорию выбираем заново под каждое меню: id категорий у меню свои.
    useEffect(() => {
        if (categoriesLoading) return;

        setTargetCategoryId((current) =>
            current !== null && targetCategories.some((category) => category.id === current)
                ? current
                : targetCategories[0]?.id ?? null
        );
    }, [targetCategories, categoriesLoading]);

    if (!item) return null;

    const submit = async () => {
        if (targetCategoryId === null) return;

        const copied = await state.mutations.copyItem(item.id, {
            targetMenuId,
            targetMenuCategoryId: targetCategoryId,
            name: { ...item.name, [state.locale]: name.trim() },
        });

        if (copied) state.closeDialog();
    };

    const canSubmit =
        name.trim().length > 0 && targetCategoryId !== null && !state.mutations.saving;

    return (
        <Modal
            open
            onClose={state.closeDialog}
            title="Копировать позицию"
            actions={
                <>
                    <Button
                        variant="outlined"
                        color="neutral"
                        disabled={state.mutations.saving}
                        onClick={state.closeDialog}
                    >
                        Отмена
                    </Button>
                    <Button variant="contained" disabled={!canSubmit} onClick={submit}>
                        Копировать
                    </Button>
                </>
            }
        >
            <Stack $gap={16}>
                <LabeledField label="Название копии">
                    <TextInput value={name} onChange={setName} />
                </LabeledField>

                <LabeledField label="Меню">
                    <Select
                        value={targetMenuId}
                        onChange={(value) => setTargetMenuId(Number(value))}
                        options={menuOptions.map((menu) => ({
                            value: menu.id,
                            label: localizedText(menu.name, state.locale),
                        }))}
                    />
                </LabeledField>

                <LabeledField
                    label="Категория"
                    hint={
                        categoriesLoading
                            ? "Загружаем категории меню…"
                            : targetCategories.length === 0
                            ? "В выбранном меню нет категорий — сначала создайте её там"
                            : undefined
                    }
                    hintTone={
                        !categoriesLoading && targetCategories.length === 0 ? "danger" : "default"
                    }
                >
                    <Select
                        value={targetCategoryId}
                        onChange={(value) => setTargetCategoryId(Number(value))}
                        options={targetCategories.map((category) => ({
                            value: category.id,
                            label: localizedText(category.name, state.locale),
                        }))}
                    />
                </LabeledField>
            </Stack>
        </Modal>
    );
};
